import type { EditorPlugin, EditorPluginOptions } from '@wix/ricos';
import {
  audioButtonsTypes,
  LinkPreviewProviders,
  pluginActionButton,
  pluginAi,
  pluginAudio,
  pluginCodeBlock,
  pluginCollapsibleList,
  pluginDivider,
  pluginEmoji,
  pluginFileUpload,
  pluginGallery,
  pluginGiphy,
  pluginHashtag,
  pluginHeadings,
  pluginHtml,
  pluginImage,
  pluginIndent,
  pluginLineSpacing,
  pluginLink,
  pluginLinkButton,
  pluginLinkPreview,
  pluginMentions,
  pluginPoll,
  pluginSpoiler,
  pluginTable,
  pluginTextColor,
  pluginTextHighlight,
  pluginVerticalEmbed,
  pluginVideo,
  SpoilerEditorWrapper,
  verticalEmbedProviders,
  videoButtonsTypes,
} from '@wix/ricos';
import type { EditorPluginName } from '@wix/ricos/schema-next';
import { mapValues } from 'lodash';

import { mockFetchUrlPreviewData } from './mocks/fetch-data-mock';
import { mockCustomAudioUploadFunc } from './mocks/file-selection-mock';
import { SocialPollsServiceMock } from './mocks/SocialPollsServiceMock';
import {
  colorScheme,
  customBackgroundStyleFn,
  customForegroundStyleFn,
  styleSelectionPredicate,
} from './mocks/text-color-style-fn';
import { MockGetIsVisiblePromise, MockVerticalSearchModule } from './mocks/verticalEmbedUtil';
import { mockCustomVideoUploadFunc } from './mocks/video-plugin-mock';

let userColors: string[] = [];

const { event, booking, product } = verticalEmbedProviders;
const { Instagram, Twitter, TikTok } = LinkPreviewProviders;

const colors = ['#FFDFBA', '#FFFFBA'];
let userButtonTextColors: string[] = colors;
let userButtonBackgroundColors: string[] = colors;
let userButtonBorderColors: string[] = colors;

const buttonConfig = {
  getTextColors: () => userButtonTextColors,
  onTextColorAdded: color => (userButtonTextColors = [...userButtonTextColors, color]),

  getBackgroundColors: () => userButtonBackgroundColors,
  onBackgroundColorAdded: color =>
    (userButtonBackgroundColors = [...userButtonBackgroundColors, color]),

  getBorderColors: () => userButtonBorderColors,
  onBorderColorAdded: color => (userButtonBorderColors = [...userButtonBorderColors, color]),
};

export const editorPlugins: Record<EditorPluginName, EditorPlugin | EditorPluginOptions> = {
  ai: pluginAi(),
  layout: { type: 'LAYOUT', showInQuickActions: false, showInToolbars: false },
  image: pluginImage({
    imageEditorWixSettings: {
      initiator: 'some-initiator',
      siteToken:
        // eslint-disable-next-line max-len
        'JWS.eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6Im5FUXljQzlOIn0.eyJpYXQiOjE1Njc1MjY3NzQsImRhdGEiOiJ7XCJ1c2VySWRcIjpcIjE5YTY0YTRjLWVlZTAtNGYxNC1iNjI3LTY3MmQ1ZjE2OGJkNFwiLFwibWV0YXNpdGVJZFwiOlwiNTM4ZmE2YzYtYzk1My00Y2RkLTg2YzQtNGI4NjlhZWNmOTgwXCJ9IiwiZXhwIjoxNTY4NzM2Mzc0fQ.n21OxIzSbqi8N3v30b6cIxMdshBnkkf2WQLWEFVXsLk',
      metaSiteId: '538fa6c6-c953-4cdd-86c4-4b869aecf980',
      mediaRoot: 'some-mediaRoot',
    },
    // eslint-disable-next-line no-console
    onImageEditorOpen: () => console.log('Media Studio Launched'),
  }),
  gallery: pluginGallery({ scrollingElement: () => window, scopingId: 'ricos-editor' }),
  poll: pluginPoll({
    showVoteRoleSetting: true,
    pollsClientApi: new SocialPollsServiceMock(),
  }),
  divider: pluginDivider(),
  spoiler: pluginSpoiler({ SpoilerEditorWrapper }),
  table: pluginTable(),
  collapsibleList: pluginCollapsibleList({ isQapageData: true }),
  lineSpacing: pluginLineSpacing(),
  video: pluginVideo({
    toolbar: {
      hidden: [],
    },
    handleFileSelection: mockCustomVideoUploadFunc,
    enableCustomUploadOnMobile: true,
    exposeButtons: [videoButtonsTypes.video, videoButtonsTypes.youTube],
  }),
  audio: pluginAudio({
    handleFileSelection: mockCustomAudioUploadFunc,
    fetchData: mockFetchUrlPreviewData(),
    exposeButtons: [
      audioButtonsTypes.audio,
      audioButtonsTypes.soundCloud,
      audioButtonsTypes.spotify,
    ],
  }),
  html: pluginHtml({
    minWidth: 35,
    maxWidth: 740,
    width: 350,
    minHeight: 50,
    maxHeight: 1200,
    exposeButtons: ['html', 'adsense'],
    siteDomain: 'https://www.wix.com',
  }),
  link: pluginLink({
    // eslint-disable-next-line no-console
    onClick: (event, url) => console.log('link clicked!', url),
    linkTypes: { anchor: true },
  }),
  hashtag: pluginHashtag({
    createHref: decoratedText => `/search/posts?query=${encodeURIComponent('#')}${decoratedText}`,
    onClick: (event, text) => {
      event.preventDefault();
      // eslint-disable-next-line no-console
      console.log(`'${text}' hashtag clicked!`);
    },
  }),
  mentions: pluginMentions({
    repositionSuggestions: true,
    visibleItemsBeforeOverflow: 5,
    zIndex: 199,
    getMentions: searchQuery =>
      new Promise(resolve =>
        setTimeout(
          () =>
            resolve(
              [
                {
                  name: 'Oded Soffrin',
                  slug: 'oddedsoffrin',
                  avatar: 'https://ca.slack-edge.com/T02T01M9Y-U0MN7A3H8-38d64543367b-192',
                },
                {
                  name: 'Alex Greenstein',
                  slug: 'alexgreenstein',
                  avatar: 'https://ca.slack-edge.com/T02T01M9Y-U6FQCJ5GT-65d722ddfc28-192',
                },
                { name: 'Yaron Nachshon', slug: 'yaronnachshon' },
                {
                  name: 'Dana Rish',
                  slug: 'danarish',
                  avatar: 'https://ca.slack-edge.com/T02T01M9Y-UEBDPSU2W-ad52085040c0-192',
                },
                {
                  name: 'Judith Gabriela Jaroslavsky',
                  slug: 'judithjaroslavsky',
                  avatar: 'https://ca.slack-edge.com/T02T01M9Y-UHMNZJ9P1-c693bb66ca93-512',
                },
                { name: 'Test One.5', slug: 'testone5' },
                { name: 'Test One.6', slug: 'testone6' },
                { name: 'Test One.7', slug: 'testone7' },
                { name: 'Test One.8', slug: 'testone8' },
                {
                  name: 'Test Two',
                  slug: 'testwo',
                  avatar: 'https://via.placeholder.com/100x100?text=Image=50',
                },
              ].filter(({ name }) => name.toLowerCase().includes(searchQuery.toLowerCase()))
            ),
          250
        )
      ),
  }),
  codeBlock: pluginCodeBlock(),
  giphy: pluginGiphy({
    getGiphyUrl: (path: 'search' | 'trending') => {
      if (typeof window === 'undefined') return undefined;
      return `https://apps.wix.com/_serverless/ricos-giphy-proxy/${path}`;
    },
  }),
  file: pluginFileUpload({
    accept: '*',
    adobeAPIKey: '77266adc759d4dc2a9e071e3bde25095',
  }),
  actionButton: pluginActionButton({
    insertButtonTooltip: 'Add an action button',
    ...buttonConfig,
  }),
  linkButton: pluginLinkButton(buttonConfig),
  textColor: pluginTextColor({
    colorScheme,
    styleSelectionPredicate,
    customStyleFn: customForegroundStyleFn,
    onColorAdded: color => (userColors = [...userColors, color]),
    getUserColors: () => userColors,
  }),
  textHighlight: pluginTextHighlight({
    colorScheme,
    styleSelectionPredicate,
    customStyleFn: customBackgroundStyleFn,
    onColorAdded: color => (userColors = [...userColors, color]),
    getUserColors: () => userColors,
  }),
  emoji: pluginEmoji(),
  verticalEmbed: pluginVerticalEmbed({
    verticalsApi: type => new MockVerticalSearchModule(type),
    exposeEmbedButtons: [product, event, booking],
    getIsVisiblePromise: (type, locale) => MockGetIsVisiblePromise(type, locale),
  }),
  heading: pluginHeadings({
    customHeadings: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P'],
    allowHeadingCustomization: true,
  }),
  indent: pluginIndent(),
  linkPreview: pluginLinkPreview({
    enableEmbed: true,
    enableLinkPreview: true,
    fetchData: mockFetchUrlPreviewData(),
    exposeEmbedButtons: [Instagram, Twitter, TikTok],
  }),
} as const;

export const editorPluginsEnabledByDefault = {
  ...Object.keys(editorPlugins).reduce((val, key) => {
    return {
      ...val,
      [key]: true,
    };
  }, {}),
  ai: false,
};

export const defaultEditorPlugins: Record<keyof typeof editorPlugins, boolean> = mapValues(
  editorPlugins,
  (_, name) => editorPluginsEnabledByDefault[name]
);
